import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Play, UserPlus, Receipt, Briefcase, Mail, Bell, FileText, Calendar } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useRealtimeSubscription } from "@/hooks/use-realtime";

interface Template {
  id: string;
  name: string;
  description: string;
  category: string;
  icon: string;
  is_active: boolean;
  is_system: boolean;
  created_at: string;
  updated_at: string;
  created_by: string;
}

interface AutomationTemplatesProps {
  onUseTemplate: (template: Template) => void;
}

const iconMap: Record<string, any> = {
  UserPlus,
  Receipt,
  Briefcase,
  Mail,
  Bell,
  FileText,
  Calendar,
};

export const AutomationTemplates = ({ onUseTemplate }: AutomationTemplatesProps) => {
  // Fetch workflow templates from database
  const { data: templates, isLoading } = useQuery({
    queryKey: ["workflow-templates"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("workflow_templates")
        .select("*")
        .eq("is_active", true)
        .order("name");

      if (error) throw error;
      return data;
    },
  });

  // Realtime subscription for templates
  useRealtimeSubscription("workflow_templates", "workflow-templates");

  if (isLoading) {
    return <div className="text-center py-8 text-muted-foreground">Loading templates...</div>;
  }

  if (!templates || templates.length === 0) {
    return (
      <Card className="p-6">
        <p className="text-muted-foreground">No templates available yet.</p>
      </Card>
    );
  }

  return (
    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
      {templates.map((template) => {
        const Icon = iconMap[template.category] || Briefcase;
        
        return (
          <Card key={template.id} className="p-6 space-y-4">
            <div className="flex items-start gap-3">
              <div className="p-2 rounded-lg bg-primary/10">
                <Icon className="h-5 w-5 text-primary" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-lg">{template.name}</h3>
                <p className="text-sm text-muted-foreground mt-1">{template.description}</p>
              </div>
            </div>
            
            <div className="flex gap-2">
              <Badge variant="secondary">{template.category}</Badge>
              {template.is_system && <Badge variant="outline">System</Badge>}
            </div>
            
            <Button onClick={() => onUseTemplate(template)} className="w-full">
              <Play className="mr-2 h-4 w-4" />
              Use Template
            </Button>
          </Card>
        );
      })}
    </div>
  );
};